import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Rocket, Check, Loader2, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';

const managedSteps = [
  { id: 'provisioning', labelKey: 'deployProgress.steps.preparingContainer' },
  { id: 'configuring', labelKey: 'deployProgress.steps.configuringOpenClaw' },
  { id: 'completed', labelKey: 'deployProgress.steps.deploymentComplete' },
];

const byosSteps = [
  { id: 'connecting', labelKey: 'deployProgress.steps.connectingSSH' },
  { id: 'installing', labelKey: 'deployProgress.steps.installingDocker' },
  { id: 'deploying', labelKey: 'deployProgress.steps.deployingContainer' },
  { id: 'verifying', labelKey: 'deployProgress.steps.verifyingDeployment' },
  { id: 'completed', labelKey: 'deployProgress.steps.deploymentComplete' },
];

export function DeployProgress() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data: progress } = useQuery({
    queryKey: ['deploy-progress', id],
    queryFn: async () => {
      const { data } = await api.get(`/deploy/${id}/progress`);
      return data;
    },
    enabled: !!id,
    refetchInterval: 2000,
  });

  // Determine hosting type from progress steps
  const isByos = progress?.step && ['connecting', 'installing', 'deploying', 'verifying'].includes(progress.step);
  const deploySteps = isByos ? byosSteps : managedSteps;

  const currentStepIndex = deploySteps.findIndex((s) => s.id === progress?.step);
  const isComplete = progress?.status === 'completed';
  const isError = progress?.status === 'error';

  return (
    <div className="max-w-2xl mx-auto space-y-8">
      <div className="text-center">
        <motion.div
          className={cn(
            'inline-flex items-center justify-center h-20 w-20 rounded-full mb-6',
            isError ? 'bg-red-500' : 'gradient-primary',
          )}
          animate={isComplete || isError ? {} : { scale: [1, 1.05, 1] }}
          transition={{ repeat: Infinity, duration: 2 }}
        >
          {isComplete ? (
            <Check className="h-10 w-10 text-white" />
          ) : (
            <Rocket className="h-10 w-10 text-white" />
          )}
        </motion.div>
        <h1 className="text-2xl font-bold mb-2">
          {isComplete
            ? t('deployProgress.complete')
            : isError
              ? t('deployProgress.failed')
              : t('deployProgress.deploying')}
        </h1>
        <p className="text-muted-foreground">
          {progress?.message || t('deployProgress.preparingSetup')}
        </p>
      </div>

      {/* Progress bar */}
      <div className="relative">
        <div className="h-2 bg-muted rounded-full overflow-hidden">
          <motion.div
            className={cn('h-full rounded-full', isError ? 'bg-red-500' : 'gradient-primary')}
            initial={{ width: 0 }}
            animate={{ width: `${progress?.progress || 0}%` }}
            transition={{ duration: 0.5 }}
          />
        </div>
        <p className="text-center text-sm text-muted-foreground mt-2">{progress?.progress || 0}%</p>
      </div>

      {/* Steps */}
      <Card>
        <CardContent className="p-6">
          <div className="space-y-4">
            {deploySteps.map((step, i) => {
              const isActive = step.id === progress?.step;
              const isDone = i < currentStepIndex || isComplete;

              return (
                <div key={step.id} className="flex items-center gap-4">
                  <div className={cn(
                    'flex items-center justify-center h-8 w-8 rounded-full text-xs font-medium flex-shrink-0',
                    isDone ? 'gradient-primary text-white' :
                    isActive ? 'border-2 border-primary text-primary' :
                    'border-2 border-muted text-muted-foreground',
                  )}>
                    {isDone ? <Check className="h-4 w-4" /> :
                     isActive ? <Loader2 className="h-4 w-4 animate-spin" /> :
                     i + 1}
                  </div>
                  <span className={cn(
                    'text-sm',
                    isDone ? 'text-foreground font-medium' :
                    isActive ? 'text-primary font-medium' :
                    'text-muted-foreground',
                  )}>
                    {t(step.labelKey)}
                  </span>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {isComplete && (
        <div className="text-center">
          <Button
            size="lg"
            className="gap-2"
            onClick={() => navigate(`/dashboard/instances/${id}`)}
          >
            {t('deployProgress.viewInstance')}
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      )}

      {isError && (
        <div className="text-center">
          <Button
            variant="outline"
            size="lg"
            onClick={() => navigate('/dashboard')}
          >
            {t('deployProgress.backToDashboard')}
          </Button>
        </div>
      )}
    </div>
  );
}
